/**
 * Built-in object shapes for Object along Path (full SVG documents, viewBox 0 0 100 100).
 */

export interface ObjectSvgPreset {
    id: string;
    label: string;
    /** Complete `<svg>...</svg>` string */
    svg: string;
}

/** Uniform square grid in viewBox 0 0 100 100, centered with equal gutters. */
function gridSquarePresetSvg(
    count: number,
    cellPx: number,
    gapPx: number,
): string {
    const step = cellPx + gapPx;
    const span = count * cellPx + (count - 1) * gapPx;
    const margin = (100 - span) / 2;
    let rects = "";
    for (let j = 0; j < count; j++) {
        for (let i = 0; i < count; i++) {
            const x = margin + i * step;
            const y = margin + j * step;
            rects += `<rect x="${x}" y="${y}" width="${cellPx}" height="${cellPx}"/>`;
        }
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><g fill="#333333">${rects}</g></svg>`;
}

export const OBJECT_SVG_PRESETS: ObjectSvgPreset[] = [
    {
        id: "square",
        label: "Square",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect x="14" y="14" width="72" height="72" fill="#333333"/></svg>`,
    },
    {
        id: "grid-3x3",
        label: "3×3 grid",
        svg: gridSquarePresetSvg(3, 16, 10),
    },
    {
        id: "grid-5x5",
        label: "5×5 grid",
        svg: gridSquarePresetSvg(5, 10, 5),
    },
    {
        id: "circle",
        label: "Circle",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="38" fill="#333333"/></svg>`,
    },
    {
        id: "dot",
        label: "Dot",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="6" fill="#333333"/></svg>`,
    },
    {
        id: "pill",
        label: "Pill",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect x="10" y="32" width="80" height="36" rx="18" ry="18" fill="#333333"/></svg>`,
    },
    {
        id: "line",
        label: "Line",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect x="10" y="46" width="80" height="8" rx="4" ry="4" fill="#333333"/></svg>`,
    },
    {
        id: "line-vertical",
        label: "Line (vertical)",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect x="46" y="10" width="8" height="80" rx="4" ry="4" fill="#333333"/></svg>`,
    },
    {
        id: "semicircle",
        label: "Semicircle",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="#333333" d="M12 50h76a38 38 0 0 0-76 0z"/></svg>`,
    },
    {
        id: "chevron",
        label: "Chevron",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="#333333" d="M18 18l48 32-48 32 17-32-17-32z"/></svg>`,
    },
    {
        id: "donut",
        label: "Donut",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="#333333" fill-rule="evenodd" d="M88 50A38 38 0 0 1 12 50A38 38 0 0 1 88 50M68 50A18 18 0 0 0 32 50A18 18 0 0 0 68 50"/></svg>`,
    },
    {
        id: "crescent",
        label: "Crescent",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="#333333" fill-rule="evenodd" d="M50 12A38 38 0 1 0 50.01 88A38 38 0 1 0 50 12M62 24A28 28 0 1 1 61.99 76A28 28 0 1 1 62 24"/></svg>`,
    },
    {
        id: "double-circle",
        label: "Double circle",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><g fill="#333333"><circle cx="36" cy="50" r="26"/><circle cx="64" cy="50" r="26"/></g></svg>`,
    },
    {
        id: "zigzag",
        label: "Zigzag",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="#333333" d="M8 78L24 22L40 78L56 22L72 78L92 22v18H76L60 78L44 34L28 78L12 34H8v44z"/></svg>`,
    },
    {
        id: "triangle",
        label: "Triangle",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><polygon points="50,12 88,86 12,86" fill="#333333"/></svg>`,
    },
    {
        id: "plus",
        label: "Plus",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect x="18" y="42" width="64" height="16" fill="#333333"/><rect x="42" y="18" width="16" height="64" fill="#333333"/></svg>`,
    },
    {
        id: "diamond",
        label: "Diamond",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><polygon points="50,8 92,50 50,92 8,50" fill="#333333"/></svg>`,
    },
    {
        id: "hexagon",
        label: "Hexagon",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><polygon points="50,10 88,30 88,70 50,90 12,70 12,30" fill="#333333"/></svg>`,
    },
    {
        id: "star",
        label: "Star",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="#333333" d="M50 8l9.2 28.3h29.8L65.4 57.3l9.2 28.3L50 64.6l-24.6 21 9.2-28.3L10.9 36.3h29.8z"/></svg>`,
    },
    {
        id: "arrow",
        label: "Arrow",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="#333333" d="M12 38h40V18l36 32-36 32V62H12z"/></svg>`,
    },
    {
        id: "cross",
        label: "Cross",
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect x="42" y="10" width="16" height="80" fill="#333333" transform="rotate(45 50 50)"/><rect x="42" y="10" width="16" height="80" fill="#333333" transform="rotate(-45 50 50)"/></svg>`,
    },
];
